import { gql } from '@apollo/client';

import { REUSE_RULE_FIELDS } from '@/pages/CreativeEditPage/ReuseRulesTab/ReuseRulesTable/queries';

export const CHANNEL_FEATURE_FLAGS_FRAGMENT = gql`
  fragment channelFeatureFlagsFragment on Client {
    hasSMSChannel: hasFeature(feature: "enable_channel_sms")
    hasMMSChannel: hasFeature(feature: "enable_channel_mms")
    hasRCSChannel: hasFeature(feature: "enable_channel_rcs")
    hasPushChannel: hasFeature(feature: "enable_channel_push")
    hasInAppChannel: hasFeature(feature: "enable_channel_inapp")
  }
`;

export const FEATURE_FLAGS_FRAGMENT = gql`
  fragment featureFlagsFragment on Client {
    hasHardRefreshClientSwitcher: hasFeature(
      feature: "hard_refresh_client_switcher"
    )
    hasMfa: hasFeature(feature: "has_mfa")
    hasCustomFields: hasFeature(feature: "has_custom_fields")
    aiGeneratedSl: hasFeature(feature: "ai_generated_sl")
    canCreateSavedView: hasFeature(feature: "create_savedView_access")
    contactReplaceImports: hasFeature(feature: "contact_replace_imports")
    analyticsV2Access: hasFeature(feature: "analytics_v2_access")
    hasCampaignProofingAgent: hasFeature(feature: "campaign_proofing_agent")
    hasAiAssistedSubjectLines: hasFeature(
      feature: "has_ai_assisted_subject_lines"
    )
    hasCreativeChannelConverterAssistant: hasFeature(
      feature: "has_creative_channel_converter_assistant"
    )
    semanticLabelsEnabled: hasFeature(feature: "semantic_labels")
    shouldDisplayAuth0MigrationAlert: hasFeature(
      feature: "auth0_migration_alert"
    )
    hasAudienceOverrides: hasFeature(feature: "has_audience_overrides")
    hasTableRelationships: hasFeature(feature: "has_table_relationships")
    hasNewAudiencesByDefault: hasFeature(
      feature: "has_new_audiences_by_default"
    )
    hasCampaignFieldOverrides: hasFeature(
      feature: "has_campaign_field_overrides"
    )
    hasIdColumnTableFields: hasFeature(feature: "has_id_column_table_fields")
    hasFrequencyToggle: hasFeature(feature: "frequency_toggle")

    hasPrograms: hasFeature(feature: "has_programs")
    hasEmailDataTypeOption: hasFeature(feature: "has_email_data_type_option")
    isAbleToDeleteTags: hasFeature(feature: "is_able_to_delete_tags")
    isAbleToExpireTags: hasFeature(feature: "is_able_to_expire_tags")
    hasToneLabelDropdown: hasFeature(feature: "show_tone_label_dropdown")
    hasUnifiedAudienceBuilder: hasFeature(
      feature: "has_unified_audience_builder"
    )
    hasExperimentAudiences: hasFeature(feature: "has_experiment_audiences")
    hasAccountPermissions: hasFeature(feature: "has_account_permissions")
    hasAccountCustomRole: hasFeature(feature: "has_account_custom_role")
    disableMixpanelSessionRecord: hasFeature(
      feature: "disable_mixpanel_session_record"
    )
    hasSubjectLineEnhancements: hasFeature(
      feature: "has_subjectline_enhancements"
    )
    hasAudienceFilters: hasFeature(feature: "has_audience_filters")
    hasSlsV2: hasFeature(feature: "has_sls_v2")
    enableAiFeatures: hasFeature(feature: "enable_ai_features")
    hasCopilot: hasFeature(feature: "has_copilot")
    hasCreativeEffectivenessV2: hasFeature(
      feature: "has_creative_effectiveness_v2"
    )
    hasAudiencesCampaignsFilter: hasFeature(
      feature: "has_audiences_campaigns_filter"
    )
    hasPrimaryContentBlockFilter: hasFeature(
      feature: "has_primary_content_block_filter"
    )
    supportMultipleEsps: hasFeature(feature: "support_multiple_esps")
    hasAnalyticsCustomEvents: hasFeature(feature: "has_analytics_custom_events")
    hasDirectCdnUpload: hasFeature(feature: "use_direct_cdn_image_upload")
    hasMobileImageUpload: hasFeature(feature: "has_mobile_image_upload")
    hasContentVariants: hasFeature(feature: "has_content_variants")
    hasChannelKey: hasFeature(feature: "has_channel_keys")
    hasNonWebsafeFontsInEditor: hasFeature(
      feature: "has_non_websafe_fonts_in_editor"
    )
    hasBackgroundImageInEditor: hasFeature(feature: "has_bg_image_in_editor")
    hasAccountPrivacy: hasFeature(feature: "has_account_privacy")
    hasAudienceUnarchiveOption: hasFeature(
      feature: "has_audience_unarchive_option"
    )
    hasCampaignAudienceOverlap: hasFeature(
      feature: "has_campaign_audience_overlap"
    )
    hasAudienceDetailPage: hasFeature(feature: "has_audience_detail_page")
    hasAnalyticsIndexRecurringExports: hasFeature(
      feature: "has_analytics_index_recurring_exports"
    )
    hasExportAnalyticsDetailPage: hasFeature(
      feature: "has_export_analytics_detail_page"
    )
    hasReportingMultichannel: hasFeature(feature: "has_reporting_multichannel")
    hasReportingPushMultichannel: hasFeature(
      feature: "has_reporting_push_multichannel"
    )
    hasDataFieldDescription: hasFeature(feature: "has_data_field_description")
    hasExperimentalAlertsRemoved: hasFeature(
      feature: "has_experimental_alerts_removed"
    )
    hasBrandKitLinkStyles: hasFeature(feature: "has_brand_kit_link_styles")
    hasBrandKitMobileStyles: hasFeature(feature: "has_brand_kit_mobile_styles")
    # TODO: feature_flag_cleanup: enableBrandKitStyleDuplication
    enableBrandKitStyleDuplication: hasFeature(
      feature: "enable_brand_kit_style_duplication"
    )
    hasEncryptionV2: hasFeature(feature: "has_encryption_v2")
    hasManagedCardSummary: hasFeature(feature: "has_managed_card_summary")
    hasRawDataSubjectRequestError: hasFeature(
      feature: "has_raw_data_subject_request_error"
    )
    hasEditorMobileControls: hasFeature(feature: "editor_has_mobile_controls")
    hasSendTimePersonalizationDisabled: hasFeature(
      feature: "has_send_time_personalization_disabled"
    )
    hasEditorComponents: hasFeature(feature: "editor_has_components")
    hasEditorVariables: hasFeature(feature: "has_editor_variables")
    # TODO: feature_flag_cleanup: hasNewDisclaimerRichtextEditor
    hasNewDisclaimerRichtextEditor: hasFeature(
      feature: "has_new_disclaimer_richtext_editor"
    )
    # TODO: feature_flag_cleanup: hasCreativeEditorV2
    hasCreativeEditorV2: hasFeature(feature: "has_creative_editor_v2")

    hasJourneyBuilder: hasFeature(feature: "journey_builder")

    hasBrandKitComponents: hasFeature(feature: "has_brand_kit_components")
    hasBrandKitBlocks: hasFeature(feature: "has_brand_kit_blocks")
    hasBrandKitAiGuidelines: hasFeature(feature: "has_brand_kit_ai_guidelines")
    hasReportingDashboard: hasFeature(feature: "has_reporting_dashboard")
    hasMessageBuilderStartFromScratch: hasFeature(
      feature: "has_message_builder_new_flow"
    )
    hasMessageBuilder: hasFeature(feature: "message_builder")
    hasMessageBuilderZones: hasFeature(feature: "message_builder_zones")
    hasMessageBuilderPreview: hasFeature(feature: "message-builder-preview")
    hasTableRowTtl: hasFeature(feature: "has_table_row_ttl")
    hasSmartStandardRerun: hasFeature(feature: "smart_standard_reruns")
    hasRecurringCampaigns: hasFeature(feature: "has_recurring_campaigns")
    hasTriggeredCampaigns: hasFeature(feature: "has_triggered_campaigns")
    hasReportingRecurringCampaigns: hasFeature(
      feature: "has_reporting_recurring_campaigns"
    )
    # TODO: feature_flag_cleanup: hasReportingTriggerCampaigns
    hasReportingTriggerCampaigns: hasFeature(
      feature: "has_reporting_trigger_campaigns"
    )
    hasTableRowCount: hasFeature(feature: "has_table_row_count")
    hasReportingDataFiltering: hasFeature(
      feature: "has_reporting_data_filtering"
    )
    hasReportingIndirectAttribution: hasFeature(
      feature: "has_reporting_indirect_attribution"
    )
    hasConfigureCreativeEligibility: hasFeature(
      feature: "has_configure_creative_eligibility"
    )
    hasDemoAudienceCounts: hasFeature(feature: "has_demo_audience_counts")
    # TODO: feature_flag_cleanup: hasDemoPositiveMetrics
    hasDemoPositiveMetrics: hasFeature(feature: "has_demo_positive_metrics")
    hasContactLevelEventExportUi: hasFeature(
      feature: "has_contact_level_event_export_ui"
    )
    hasDemoFriendlyUI: hasFeature(feature: "has_demo_friendly_ui")
    # TODO: feature_flag_cleanup: hasEligibilityTabUxUpdates
    hasEligibilityTabUxUpdates: hasFeature(feature: "has_disable_reuse_rules")
    # TODO: feature_flag_cleanup: hasContactBasedMetrics
    hasContactBasedMetrics: hasFeature(feature: "has_contact_based_metrics")
    # TODO: feature_flag_cleanup: hasCreativeTagHeatmap
    hasCreativeTagHeatmap: hasFeature(feature: "has_creativetag_heatmap")
    # TODO: feature_flag_cleanup: hasCreativePurpose
    hasCreativePurpose: hasFeature(feature: "has_creative_purpose")
    # TODO: feature_flag_cleanup: hasDatasetsUi
    hasDatasetsUi: hasFeature(feature: "has_datasets_ui")
    # TODO: feature_flag_cleanup: hasConfigurableKpi
    hasConfigurableKpi: hasFeature(feature: "has_configurable_kpi")
    hasDynamicAudiences: hasFeature(feature: "has_value_mode_dates_in_audience")
    # TODO: feature_flag_cleanup: hasPushTrackingParams
    hasPushTrackingParams: hasFeature(feature: "has_push_tracking_params")
    # TODO: feature_flag_cleanup: hasUnifiedMessagingNav
    hasUnifiedMessagingNav: hasFeature(feature: "has_unified_messaging_nav")
    # TODO: feature_flag_cleanup: hasUserLifecycle
    hasUserLifecycle: hasFeature(feature: "has_user_lifecycle")
    # TODO: feature_flag_cleanup: hasUnlockComponents
    hasUnlockComponents: hasFeature(feature: "has_unlock_components")
    # TODO: feature_flag_cleanup: hasAiAudienceBuilder
    hasAiAudienceBuilder: hasFeature(feature: "has_ai_audience_builder")
    # TODO: feature_flag_cleanup: hasPlatformApplicationPermission
    hasPlatformApplicationPermission: hasFeature(
      feature: "has_platform_application_permission"
    )
  }
`;

export const CURRENT_SESSION_QUERY = gql`
  query CurrentSession {
    availableClients {
      id
      displayName
    }
    currentUser {
      id
      isStaff
      initials
      fullName
      email
      featureAccess
      hasSeenCampaignV2Intro
      isSuspended
      isDeveloper
    }
  }
`;

export const SESSION_ORGANIZATION_USER_QUERY = gql`
  query CurrentOrganizationUser {
    currentOrganizationUser {
      organization {
        id
      }
      accountMembers {
        account {
          id
          name
          displayName
        }
        roles {
          id
          name
          title
        }
        status
        inviteExpiresAt
        invitedBy {
          firstName
          lastName
          emailAddress
        }
        isOwner
        isAdmin
      }
      isAdmin
      isOwner
      isSuperuser
    }
  }
`;

export const ESP_METADATA = gql`
  fragment espMetadataFields on EmailServiceProvider {
    __typename
    ... on AcousticCampaign {
      id
      displayName
    }
    ... on AdobeCampaign {
      id
      displayName
    }
    ... on AdobeJourneyOptimizer {
      id
      displayName
    }
    ... on AmazonSES {
      id
      displayName
    }
    ... on Braze {
      id
      displayName
    }
    ... on Cordial {
      id
      displayName
    }
    ... on Epsilon {
      id
      displayName
    }
    ... on OracleResponsys {
      id
      displayName
      linkCategories {
        id
        title
      }
    }
    ... on SalesForceMarketingCloud {
      id
      displayName
    }
    ... on Zeta {
      id
      displayName
    }
    ... on AmazonSES {
      id
      displayName
    }
    ... on Bluecore {
      id
      displayName
    }
    ... on Blackhole {
      id
      displayName
    }
    ... on SFNext {
      id
      displayName
    }
  }
`;

export const CURRENT_CLIENT = gql`
  ${FEATURE_FLAGS_FRAGMENT}
  ${CHANNEL_FEATURE_FLAGS_FRAGMENT}
  ${ESP_METADATA}
  ${REUSE_RULE_FIELDS}
  query CurrentClient {
    currentClient {
      id
      name
      displayName
      squareLogo
      timezone
      defaultStoInterval
      analyticsCalendar
      analyticsCurrency
      analyticsStatSigThreshold
      analyticsAttributionWindow
      analyticsExportDecimalPrecision
      analyticsExportRateFormat
      autoInclude
      emailServiceProviders {
        ...espMetadataFields
      }
      defaultBlockConfiguration {
        mode
        personalization
      }
      emailServiceProvider {
        ...espMetadataFields
      }
      fiscalCalendar {
        currentWeek
      }
      enablePersadoCampaign
      enableProofing
      enableNewProofRecommendationRun
      enableConversionTracking
      desktopEmailWidth
      mobileEmailWidth
      ...featureFlagsFragment
      ...channelFeatureFlagsFragment
      reuseRule {
        ...reuseRuleFields
      }
      tones
      primarySubjectLineRequired
      preheaderTextSetting
      enableDisclaimerLayouts
      enforceLinkDomainAllowlist
      linkDomainAllowlist
      canUploadImages
      hasCdnConfig
    }
  }
`;

export const GET_USER_PERMISSIONS_FOR_ACCOUNT = gql`
  query GetUserPermissionsForAccount($clientId: ID!) {
    userPermissionsForAccount(clientId: $clientId) {
      permissions {
        name
        title
        description
      }
    }
  }
`;
